import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Copy, Download, FileSpreadsheet, ListTree, Loader2, Plus, RefreshCw, Tags,
} from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import {
  ActiveFilters, AsyncMultiSelect, FilterBar, MultiSelect, SearchInput,
} from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtNumber } from '@/shared/lib/format'
import { countActive, type QueryValue } from '@/shared/lib/query'
import { cn } from '@/lib/utils'
import type { VoterResponse } from '@/shared/api/types'
import {
  listVoters, voterStatistics, REGISTRATION_LABEL, SUPPORT_BAND_LABEL,
  VERIFICATION_LABEL, type VoterFilters,
} from './api'
import {
  ContactCell, IntelCell, RiskCell, SupportCell, TransferCell, VerificationCell,
} from './cells'
import { VoterDrawer } from './VoterDrawer'
import { BulkTagDialog } from './BulkTagDialog'
import { useTagVocabulary } from './tags'
import { VoterFormDialog } from './VoterFormDialog'
import { DuplicateReview, VoterImportWizard } from './VoterImportWizard'
import { UnitCascade, type CascadeValue } from './UnitCascade'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { listDuplicates, runExport, EXPORT_COLUMNS } from './api'
import { toast } from '@/shared/ui/confirm'
import { explain } from '@/shared/api/problem'

/** Nine data columns, plus the selection box when the caller may bulk-tag. */
const DATA_COLUMNS = 9

const SUPPORT_BANDS = ['SUPPORTER', 'LEAN_SUPPORT', 'UNDECIDED', 'LEAN_OPPOSED', 'OPPOSED', 'UNASSESSED']
const REGISTRATION = ['REGISTERED', 'PENDING', 'TRANSFERRED_OUT', 'DECEASED', 'INELIGIBLE', 'SUSPENDED']
const VERIFICATION = [
  'UNVERIFIED', 'PHONE_VERIFIED', 'FIELD_VERIFIED', 'ID_SIGHTED',
  'IEBC_MATCHED', 'IPRS_MATCHED', 'FAILED',
]
const RISK = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

/** A card value that is honest about not knowing yet. */
const num = (n?: number | false | null) =>
  n === undefined || n === null || n === false ? '—' : fmtNumber(n)

const opts = (values: string[], labels: Record<string, string>) =>
  values.map((v) => ({ value: v, label: labels[v] ?? v }))

/**
 * Voter Registry.
 *
 * **It lists on arrival.** An earlier revision refused to load without a filter,
 * on the reasoning that nobody should browse the register. That rule was dropped
 * (backend prompt 23 §1): what actually protects the register is the scope
 * predicate, the row carrying no PII, and the bulk-read audit — none of which
 * depended on the user guessing a filter first. Making a coordinator invent a
 * filter to see their own ward was a usability tax, not a control.
 *
 * Geography is one cascade — County → Constituency → Ward → Station — and every
 * level sets the same `unitId` param, because the backend resolves any unit to
 * its ltree path. Four selects, one predicate.
 *
 * Filters live in the URL, so a narrowed register is a shareable link and the
 * back button works.
 */
export function VotersPage() {
  const canRead = useCan('voter:read')
  const canCreate = useCan('voter:create')
  const canImport = useCan('voter:import')
  const canMerge = useCan('voter:merge')
  const canExport = useCan('voter:export')
  const [exporting, setExporting] = useState(false)
  const [params, setParams] = useSearchParams()

  const [search, setSearch] = useState(params.get('q') ?? '')
  const q = useDebounced(search, 300)

  const filters: VoterFilters = useMemo(() => ({
    q: q.trim() || undefined,
    unitId: params.get('unitId') ?? undefined,
    stationId: params.getAll('stationId').length ? params.getAll('stationId') : undefined,
    wardId: params.get('wardId') ?? undefined,
    segmentCode: params.get('segment') ?? undefined,
    supportBand: params.get('supportBand') ?? undefined,
    registrationStatus: params.get('registrationStatus') ?? undefined,
    verificationStatus: params.get('verificationStatus') ?? undefined,
    tag: params.get('tag') ?? undefined,
    riskBand: params.get('riskBand') ?? undefined,
    hasOpenIntel: params.get('hasOpenIntel') === '1' ? true : undefined,
  }), [params, q])

  /**
   * Station chips live in the URL as a parallel pair of params: the ids do the
   * filtering, the labels keep the control readable. Storing only ids would mean a
   * shared link opens with "3 selected" and no way to say which three without a lookup
   * per id.
   */
  const stationChips = useMemo(() => {
    const ids = params.getAll('stationId')
    const names = params.getAll('stationName')
    return ids.map((value, i) => ({ value, label: names[i] ?? value.slice(0, 8) }))
  }, [params])

  const setStationFilter = (next: { value: string; label: string }[]) => {
    const sp = new URLSearchParams(params)
    sp.delete('stationId')
    sp.delete('stationName')
    for (const s of next) { sp.append('stationId', s.value); sp.append('stationName', s.label) }
    setParams(sp, { replace: true })
    setStack([])
  }

  /** Rebuilt from the URL so a shared link repopulates the selects. */
  const cascade: CascadeValue = useMemo(() => {
    const raw = params.get('unitPath')
    if (!raw) return { unitId: params.get('unitId') ?? undefined, path: [] }
    try {
      const path = JSON.parse(raw) as CascadeValue['path']
      return { unitId: path[path.length - 1]?.id, path }
    } catch { return { unitId: undefined, path: [] } }
  }, [params])

  // Cursor paging: cursors point forward only, so "back" walks a stack the page
  // keeps rather than a page number it computes.
  const [stack, setStack] = useState<string[]>([])
  const cursor = stack[stack.length - 1]

  const set = useCallback((key: string, value?: string) => {
    const sp = new URLSearchParams(params)
    if (value === undefined || value === '') sp.delete(key)
    else sp.set(key, value)
    setParams(sp, { replace: true })
    // The cursor stack and the selection are both cleared by the effect above,
    // which watches the filters themselves rather than each control that sets them.
  }, [params, setParams])

  const single = (key: string, selected: string[]) =>
    set(key, selected.length > 0 ? selected[0] : undefined)

  const list = useLoad(() => listVoters(filters, cursor), [JSON.stringify(filters), cursor])
  // The same filters go to the summary, so the cards are always a summary OF the
  // rows below them rather than a national total sitting over one ward.
  const stats = useLoad(() => voterStatistics(filters), [JSON.stringify(filters)])

  const canTag = useCan('voter:tag')
  const tagVocab = useTagVocabulary()

  /**
   * Row selection, for the bulk tag path.
   *
   * A `Set` keyed by voter id and NOT cleared when the page changes, so an
   * operator can walk three pages and tag the lot in one request. It IS cleared
   * when the filters change, because a selection made under one filter and
   * applied under another is a selection nobody can describe afterwards.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulk, setBulk] = useState<'apply' | 'remove' | null>(null)

  /**
   * The invariant, enforced in ONE place.
   *
   * It used to live inside `set()`, which is only one of five ways the filters
   * change — the station chips, the geography cascade, "clear all" and the search
   * box all bypassed it. Select forty voters in Ward A, switch the cascade to
   * Ward B, and the bar still said "40 selected" over forty rows that were no
   * longer on screen, ready to bulk-tag them. Keying off the serialised filters
   * means every path is covered whether or not its author remembered.
   */
  const filterKey = JSON.stringify(filters)
  const lastFilterKey = useRef(filterKey)
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return
    lastFilterKey.current = filterKey
    setSelected(new Set())
    setStack([])
  }, [filterKey])

  const toggleRow = (id: string) => setSelected((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  // `?open=<id>` is honoured so a link can land directly on one voter — the
  // identity lookup's "this person is already registered" button relies on it,
  // and without it that button navigates to an unchanged list.
  const [openId, setOpenId] = useState<string | null>(() => params.get('open'))

  useEffect(() => {
    const wanted = params.get('open')
    if (wanted && wanted !== openId) setOpenId(wanted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])
  const [createOpen, setCreateOpen] = useState(false)

  const rawView = params.get('view')
  const view = rawView === 'import' || rawView === 'duplicates' ? rawView : 'register'
  const setView = (id: string) => {
    const sp = new URLSearchParams(params)
    if (id === 'register') sp.delete('view'); else sp.set('view', id)
    setParams(sp, { replace: true })
  }
  const duplicates = useLoad(
    () => (view === 'duplicates' && canMerge ? listDuplicates() : Promise.resolve([])),
    [view, canMerge],
  )

  /**
   * Export what is on screen.
   *
   * The filter sent is the SAME object the list is querying with, so the file is exactly
   * the rows in front of you — an export that quietly widened to the whole register would
   * be the most dangerous button on the page. Scope is applied server-side on top, as
   * everywhere else, so this cannot reach past the caller's units.
   */
  async function runFilteredExport() {
    setExporting(true)
    try {
      const { rowCount } = await runExport(
        filters as unknown as Record<string, unknown>, [...EXPORT_COLUMNS])
      toast('good', rowCount === null ? 'Export ready' : `Exported ${rowCount} voters`)
    } catch (err) {
      toast('bad', explain(err))
    } finally {
      setExporting(false)
    }
  }

  if (!canRead) return <Forbidden what="the voter register" />

  const rows = list.data?.content ?? []
  const s = stats.data
  const activeCount = countActive(filters as Record<string, QueryValue>)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Voter Registry"
        description="Everyone you hold a record on, within your scope. Narrow by geography, support, verification or intelligence."
        right={
          <>
            <button
              type="button"
              className="erp-btn"
              onClick={() => { void list.reload(); void stats.reload() }}
              aria-label="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            <Link to="/voters/tags" className="erp-btn" aria-label="Voter tags">
              <Tags className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Tags</span>
            </Link>
            {canExport && (
              <button
                type="button"
                className="erp-btn"
                disabled={exporting}
                aria-label="Export"
                title="Exports exactly the rows the filters above are showing"
                onClick={() => { void runFilteredExport() }}
              >
                {exporting
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Download className="h-3.5 w-3.5" />}
                <span className="axis-hide-sm">Export</span>
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                onClick={() => setCreateOpen(true)}
                aria-label="Add voter"
              >
                <Plus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Add voter</span>
              </button>
            )}
          </>
        }
      />

      {(canImport || canMerge) && (
        <div className="border-b border-border">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'register', label: 'Register', icon: ListTree, on: true },
              { id: 'import', label: 'Import', icon: FileSpreadsheet, on: canImport },
              { id: 'duplicates', label: 'Duplicates', icon: Copy, on: canMerge },
            ].filter((t) => t.on).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setView(t.id)}
                className={cn(
                  'px-3 py-2 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap',
                  view === t.id
                    ? 'border-chocolate text-chocolate font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {view === 'import' && <VoterImportWizard />}

      {view === 'duplicates' && (
        <>
          <ErrorStrip message={duplicates.error} />
          <DuplicateReview
            candidates={duplicates.data ?? []}
            onMerged={() => { void duplicates.reload(); void list.refresh() }}
          />
        </>
      )}

      {/* Cards render immediately with placeholders rather than appearing once the
          summary lands — a strip that pops in shifts the table under the cursor.
          They summarise the SAME filters as the list, so they never disagree with
          the rows below (backend prompt 23 §2). */}
      {view === 'register' && (
        <KpiStrip>
          <MetricCard
            label={activeCount > 0 ? 'In this view' : 'In your scope'}
            value={num(s?.total)}
            accent
          />
          <MetricCard
            label="Contactable"
            value={num(s?.contactable)}
            sub={s ? `${fmtNumber(s.suppressed)} suppressed` : undefined}
          />
          <MetricCard
            label="Supporters"
            value={num(s?.bySupportBand?.SUPPORTER)}
            sub={s ? `${fmtNumber(s.bySupportBand?.UNASSESSED ?? 0)} never assessed` : undefined}
          />
          <MetricCard
            label="Verified"
            value={num(s && (s.byVerificationStatus?.FIELD_VERIFIED ?? 0)
              + (s.byVerificationStatus?.ID_SIGHTED ?? 0)
              + (s.byVerificationStatus?.IEBC_MATCHED ?? 0)
              + (s.byVerificationStatus?.IPRS_MATCHED ?? 0))}
            sub={s ? `${fmtNumber(s.byVerificationStatus?.UNVERIFIED ?? 0)} unverified` : undefined}
          />
        </KpiStrip>
      )}

      {view === 'register' && (
      <div className="erp-card px-3 py-2.5">
        <UnitCascade
          value={cascade}
          onChange={(next) => {
            const sp = new URLSearchParams(params)
            if (next.unitId) {
              sp.set('unitId', next.unitId)
              sp.set('unitPath', JSON.stringify(next.path))
            } else {
              sp.delete('unitId')
              sp.delete('unitPath')
            }
            setParams(sp, { replace: true })
            setStack([])
          }}
        />
      </div>
      )}

      {view === 'register' && (
      <FilterBar
        activeCount={activeCount}
        right={<ActiveFilters count={activeCount} onClear={() => {
          setSearch('')
          setParams({}, { replace: true })
          setStack([])
        }} />}
      >
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name"
        />
        <MultiSelect
          label="Support" emptyLabel="Any"
          options={opts(SUPPORT_BANDS, SUPPORT_BAND_LABEL)}
          selected={filters.supportBand ? [filters.supportBand] : []}
          onChange={(v) => single('supportBand', v)}
        />
        <MultiSelect
          label="Registration" emptyLabel="Any"
          options={opts(REGISTRATION, REGISTRATION_LABEL)}
          selected={filters.registrationStatus ? [filters.registrationStatus] : []}
          onChange={(v) => single('registrationStatus', v)}
        />
        <MultiSelect
          label="Verification" emptyLabel="Any"
          options={opts(VERIFICATION, VERIFICATION_LABEL)}
          selected={filters.verificationStatus ? [filters.verificationStatus] : []}
          onChange={(v) => single('verificationStatus', v)}
        />
        <MultiSelect
          label="Risk" emptyLabel="Any"
          options={RISK.map((r) => ({ value: r, label: r[0] + r.slice(1).toLowerCase() }))}
          selected={filters.riskBand ? [filters.riskBand] : []}
          onChange={(v) => single('riskBand', v)}
        />
        <MultiSelect
          label="Intel" emptyLabel="Any"
          options={[{ value: '1', label: 'Has open alerts' }]}
          selected={filters.hasOpenIntel ? ['1'] : []}
          onChange={(v) => set('hasOpenIntel', v.length ? '1' : undefined)}
        />
        {/* ONE tag, not many. `VoterFilter.tag` is a single `String` on the server,
            so a multi-select here would imply an AND/OR the backend cannot express
            and would quietly drop everything after the first choice. */}
        <MultiSelect
          label="Tag" emptyLabel="Any"
          options={(tagVocab.tags ?? []).map((t) => ({
            value: t.tag,
            label: `${t.displayName} · ${fmtNumber(t.voterCount)}`,
          }))}
          selected={filters.tag ? [filters.tag] : []}
          onChange={(v) => single('tag', v)}
        />
        {/* Stations cannot be a fixed option list — there are tens of thousands — so
            this one searches. The chosen ones keep their names in the URL beside their
            ids, because a shared link has to show what it is filtering by without
            resolving forty uuids first. */}
        <AsyncMultiSelect
          label="Station" emptyLabel="Any"
          selected={stationChips}
          onChange={setStationFilter}
          placeholder="Search polling stations"
          search={async (term) => {
            const page = await listUnits({ q: term, type: ['POLLING_STATION'] } as never, 0, 10)
            return page.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>
      )}

      {view === 'register' && (
      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error ?? stats.error} />

        <>
            <ErpTable head={<>
              {canTag && (
                <Th className="w-8">
                  {/* Selects the CURRENT PAGE, and says so. A checkbox that silently
                      meant "everything matching the filter" would be a bulk write on a
                      set the operator never saw. */}
                  <input
                    type="checkbox"
                    aria-label="Select every voter on this page"
                    title="Select every voter on this page"
                    // Some-but-not-all has to LOOK like some-but-not-all. An unchecked
                    // box over a part-selected page says "nothing here is selected",
                    // and clicking it then adds fifty rows when the reader expected
                    // it to clear three.
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = rows.some((v) => selected.has(v.id))
                          && !rows.every((v) => selected.has(v.id))
                      }
                    }}
                    checked={rows.length > 0 && rows.every((v) => selected.has(v.id))}
                    onChange={(e) => setSelected((prev) => {
                      const next = new Set(prev)
                      for (const v of rows) {
                        if (e.target.checked) next.add(v.id); else next.delete(v.id)
                      }
                      return next
                    })}
                  />
                </Th>
              )}
              <Th>Name</Th>
              <Th className="axis-hide-md">Polling station</Th>
              <Th>Support</Th>
              <Th className="axis-hide-md">Verification</Th>
              <Th>Contact</Th>
              {/* Risk and Intel are the whole reason `voter_intel_overlay` reaches
                  the row, so they stay visible at 1440. The station is a bare id
                  until the backend serves a name — a UUID fragment in a column is
                  noise, and it lives in the drawer instead. */}
              <Th>Risk</Th>
              <Th>Intel</Th>
              <Th className="axis-hide-md">Transfer</Th>
              <Th />
            </>}>
              {list.loading && <LoadingRows colSpan={DATA_COLUMNS + (canTag ? 1 : 0)} />}
              {!list.loading && rows.length === 0 && (
                <tr>
                  <td colSpan={DATA_COLUMNS + (canTag ? 1 : 0)} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
                    {activeCount > 0
                      ? 'No voters match these filters.'
                      : 'No voters in your scope yet. The register starts empty — import it, or add someone.'}
                  </td>
                </tr>
              )}
              {!list.loading && rows.map((v: VoterResponse) => (
                <tr
                  key={v.id}
                  className={cn('hover:bg-muted/40 cursor-pointer', selected.has(v.id) && 'bg-chocolate-soft/30')}
                  onClick={() => setOpenId(v.id)}
                >
                  {canTag && (
                    <Td className="w-8">
                      {/* `stopPropagation`, or ticking a box opens the drawer. */}
                      <input
                        type="checkbox"
                        aria-label={`Select ${v.displayName}`}
                        checked={selected.has(v.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleRow(v.id)}
                      />
                    </Td>
                  )}
                  <Td className="font-medium">
                    {v.displayName}
                    {v.statusCode === 'MERGED' && (
                      <span className="ml-1.5 text-erp-xs text-muted-foreground">(merged)</span>
                    )}
                  </Td>
                  <Td className="axis-hide-md">
                    {/* The server resolves the name (prompt 29/32). Until it does, the
                        id is not worth a column — an unresolved station says nothing,
                        so it says nothing rather than showing a uuid fragment. */}
                    {v.pollingStationName
                      ?? <span className="text-muted-foreground/50">—</span>}
                  </Td>
                  <Td><SupportCell voter={v} /></Td>
                  <Td className="axis-hide-md"><VerificationCell status={v.verificationStatusCode} /></Td>
                  <Td><ContactCell voter={v} /></Td>
                  <Td><RiskCell voter={v} /></Td>
                  <Td><IntelCell voter={v} /></Td>
                  <Td className="axis-hide-md"><TransferCell voter={v} /></Td>
                  <Td className="text-right">
                    <span className="text-erp-xs text-chocolate">Open</span>
                  </Td>
                </tr>
              ))}
            </ErpTable>

            <CursorPager
              page={stack.length}
              count={rows.length}
              hasMore={list.data?.hasMore ?? false}
              canGoBack={stack.length > 0}
              onBack={() => setStack((st) => st.slice(0, -1))}
              onNext={() => {
                const next = list.data?.nextCursor
                if (next) setStack((st) => [...st, next])
              }}
              loading={list.loading}
              unit="voters"
            />
        </>
      </Panel>
      )}

      {/* The bar appears only when something is selected, and it is the only thing
          that can act on a set the operator built by hand. It sits at the bottom of
          the viewport because the selection is built by scrolling. */}
      {canTag && selected.size > 0 && (
        <div className="sticky bottom-3 z-20 mx-auto w-fit rounded-md border border-border bg-popover shadow-lg px-3 py-2 flex flex-wrap items-center gap-2">
          <span className="text-erp-sm font-medium tabular-nums">
            {fmtNumber(selected.size)} selected
          </span>
          <button type="button" className="erp-btn-ghost text-erp-xs"
            onClick={() => setSelected(new Set())}>Clear</button>
          <span className="w-px h-5 bg-border" aria-hidden />
          <button type="button" className="erp-btn" onClick={() => setBulk('apply')}>
            <Tags className="h-3.5 w-3.5" /> Add a tag
          </button>
          <button type="button" className="erp-btn" onClick={() => setBulk('remove')}>
            Remove a tag
          </button>
        </div>
      )}

      {bulk && (
        <BulkTagDialog
          mode={bulk}
          selectedIds={[...selected]}
          onClose={() => setBulk(null)}
          onDone={() => { void list.refresh(); void stats.refresh(); tagVocab.reload() }}
        />
      )}

      <VoterDrawer
        voterId={openId}
        onClose={() => {
          setOpenId(null)
          if (params.get('open')) {
            const sp = new URLSearchParams(params)
            sp.delete('open')
            setParams(sp, { replace: true })
          }
        }}
        onChanged={() => { void list.refresh(); void stats.refresh() }}
      />

      <VoterFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => { void list.reload(); void stats.reload() }}
      />
    </div>
  )
}

export default VotersPage
